import { canAccessInternalRoute } from '@/config/route-access'
import { useAccessControl } from '@/hooks/use-access-control'
import { useAuth } from '@/hooks/use-auth'
import { useCoachConversations } from '@/hooks/use-coach-booking'
import { useCoachVetting } from '@/hooks/use-coach-profile'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { flushCoachAutosaves } from '@/lib/coach-autosave'
import { coachProfileReviewNeedsAction } from '@/lib/coach-profile-review'
import { cn } from '@/lib/utils'
import {
  CalendarDays,
  Home,
  LogOut,
  Menu,
  MessageCircle,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

const coachNavItems = [
  { name: 'Overview', href: '/dashboard', icon: Home, routeId: 'coach.root' as const },
  { name: 'Clients', href: '/clients', icon: UsersRound, routeId: 'coach.root' as const },
  {
    name: 'Availability',
    href: '/availability',
    icon: CalendarDays,
    routeId: 'coach.root' as const,
  },
  { name: 'Messages', href: '/messages', icon: MessageCircle, routeId: 'coach.root' as const },
  { name: 'My profile', href: '/profile', icon: UserRound, routeId: 'coach.root' as const },
]

export function CoachLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { logout, isLoggingOut, user } = useAuth()
  const { data: conversations = [] } = useCoachConversations()
  const { data: vetting } = useCoachVetting()
  const authorization = useAccessControl()
  const conversationsNeedingAttention = conversations.filter(
    (thread) => thread.new_assignment || thread.unread_count > 0,
  ).length
  const activeClientCount = conversations.filter(
    (thread) => thread.assignment_id !== null && !thread.archived_at,
  ).length
  const profileNeedsAction = coachProfileReviewNeedsAction(vetting)
  const visibleCoachNavItems = authorization.data
    ? coachNavItems.filter((item) => canAccessInternalRoute(authorization.data, item.routeId))
    : []
  const activeNavItem = coachNavItems.find(
    (item) => location.pathname === item.href || location.pathname.startsWith(`${item.href}/`),
  )
  useDocumentTitle(`${activeNavItem?.name ?? 'Coach'} · Pelgo Coach`)

  const handleLogout = async () => {
    try {
      await flushCoachAutosaves()
      await logout()
      navigate('/login', { replace: true })
    } catch {
      // The shared session banner explains that secure logout did not complete.
    }
  }

  const sidebar = (
    <>
      <div className="flex h-20 items-center justify-between border-b border-white/10 px-6">
        <Link
          to="/dashboard"
          className="flex items-center gap-3"
          onClick={() => setMobileOpen(false)}
        >
          <img src="/logomark-white.png" alt="Pelgo" className="h-[25px] w-auto" />
          <span className="hidden h-5 w-px bg-white/20 lg:block" aria-hidden="true" />
          <span className="hidden text-sm font-medium text-slate-300 lg:inline">Coach</span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="shrink-0 rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white lg:hidden"
          aria-label="Close navigation"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="px-4 pt-7">
        <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          Workspace
        </p>
        <nav className="mt-3 space-y-1">
          {visibleCoachNavItems.map((item) => {
            const Icon = item.icon
            const isActive =
              location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-white/[0.07] text-white'
                    : 'text-slate-400 hover:bg-white/[0.05] hover:text-slate-100',
                )}
              >
                <span
                  className={cn(
                    'absolute inset-y-2 left-0 w-0.5 rounded-full transition-colors',
                    isActive ? 'bg-brand' : 'bg-transparent',
                  )}
                  aria-hidden="true"
                />
                <Icon
                  className={cn(
                    'h-[18px] w-[18px] transition-colors',
                    isActive ? 'text-sky-300' : 'text-slate-500 group-hover:text-slate-300',
                  )}
                />
                <span>{item.name}</span>
                {item.href === '/clients' && activeClientCount > 0 && (
                  <span
                    className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/10 px-1.5 text-[10px] font-bold text-sky-200"
                    aria-label={`${activeClientCount} active client${activeClientCount === 1 ? '' : 's'}`}
                  >
                    {activeClientCount > 99 ? '99+' : activeClientCount}
                  </span>
                )}
                {item.href === '/messages' && conversationsNeedingAttention > 0 && (
                  <span
                    className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-400 px-1.5 text-[10px] font-bold text-[#07163b]"
                    aria-label={`${conversationsNeedingAttention} conversations need attention`}
                  >
                    {conversationsNeedingAttention > 9 ? '9+' : conversationsNeedingAttention}
                  </span>
                )}
                {item.href === '/profile' && profileNeedsAction && (
                  <span
                    className="ml-auto h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-[#07163b]"
                    aria-label="Profile action needed"
                    title="Profile action needed"
                  />
                )}
              </Link>
            )
          })}
        </nav>
      </div>

      <div className="mt-auto border-t border-white/10 p-4">
        <div className="flex items-center gap-3 rounded-xl p-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-semibold text-[#07163b]">
            {(user?.first_name?.[0] || user?.email?.[0] || 'C').toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user?.full_name || user?.first_name || 'Pelgo coach'}
            </p>
            <p className="truncate text-xs text-slate-400">{user?.email}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-slate-950 lg:flex">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-[#07163b] lg:flex">
        {sidebar}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          />
          <aside className="relative flex h-full w-[min(82vw,18rem)] flex-col bg-[#07163b] shadow-2xl">
            {sidebar}
          </aside>
        </div>
      )}
      <div className="min-w-0 flex-1 lg:ml-64">
        <header className="sticky top-0 z-30 flex h-16 items-center border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-lg border border-slate-200 p-2 text-slate-700"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src="/logomark.png" alt="Pelgo" className="ml-3 h-[23px] w-auto" />
          <span className="mx-2 h-5 w-px bg-slate-200" aria-hidden="true" />
          <span className="text-sm font-medium text-slate-500">Coach</span>
        </header>
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
